import {
  NEIGHBORHOOD_STATE_SCHEMA_VERSION,
  type NeighborhoodState,
  type ReputationTag,
  type WitnessedReputationEvent,
} from "./types.ts";

const EMPTY_REPUTATION: Readonly<Record<ReputationTag, number>> = {
  helpful: 0,
  reliable: 0,
  neighbourly: 0,
  nosy: 0,
  show_off: 0,
  odd: 0,
};

export type NeighborhoodAction =
  | "accept_ona_parcel"
  | "inspect_parcel"
  | "deliver_ruta_sealed"
  | "deliver_ruta_opened"
  | "entrust_mantas";

export function createNeighborhoodState(): NeighborhoodState {
  return {
    schemaVersion: NEIGHBORHOOD_STATE_SCHEMA_VERSION,
    quest: { status: "not_started", parcel: "none", outcome: "none" },
    reputation: { ...EMPTY_REPUTATION },
    witnessedEvents: [],
    memoriesByResidentId: {},
    access: {
      blueGate: false,
      archiveConversation: false,
      rooftopShortcut: false,
    },
  };
}

function remember(
  state: Readonly<NeighborhoodState>,
  residentId: string,
  memory: string,
): NeighborhoodState["memoriesByResidentId"] {
  return {
    ...state.memoriesByResidentId,
    [residentId]: [
      ...(state.memoriesByResidentId[residentId] ?? []),
      memory,
    ],
  };
}

function witnessed(
  state: Readonly<NeighborhoodState>,
  event: WitnessedReputationEvent,
): Pick<NeighborhoodState, "reputation" | "witnessedEvents"> {
  return {
    reputation: {
      ...state.reputation,
      [event.tag]: state.reputation[event.tag] + 1,
    },
    witnessedEvents: [...state.witnessedEvents, event],
  };
}

export function applyNeighborhoodAction(
  state: Readonly<NeighborhoodState>,
  action: NeighborhoodAction,
): NeighborhoodState {
  if (action === "accept_ona_parcel") {
    if (state.quest.status !== "not_started") return state;
    return {
      ...state,
      quest: { status: "carrying", parcel: "sealed", outcome: "none" },
      memoriesByResidentId: remember(state, "ona-vaitkute", "accepted-key"),
    };
  }
  if (action === "inspect_parcel") {
    if (state.quest.status !== "carrying" || state.quest.parcel !== "sealed") {
      return state;
    }
    return {
      ...state,
      quest: { ...state.quest, parcel: "opened" },
      memoriesByResidentId: remember(state, "mantas-simkus", "saw-open-parcel"),
      ...witnessed(state, {
        id: "courtyard-key:opened-before-mantas",
        tag: "nosy",
        witnessIds: ["mantas-simkus"],
        source: "quest",
      }),
    };
  }
  if (action === "deliver_ruta_sealed") {
    if (state.quest.status !== "carrying" || state.quest.parcel !== "sealed") {
      return state;
    }
    const reliable = witnessed(state, {
      id: "courtyard-key:ruta-sealed",
      tag: "reliable",
      witnessIds: ["ruta-kalniene"],
      source: "quest",
    });
    const withNeighbourly = witnessed(
      { ...state, ...reliable },
      {
        id: "courtyard-key:ona-reported-delivery",
        tag: "neighbourly",
        witnessIds: ["ona-vaitkute", "antanas-valeika"],
        source: "quest",
      },
    );
    return {
      ...state,
      ...withNeighbourly,
      quest: {
        status: "completed",
        parcel: "delivered",
        outcome: "delivered_sealed",
      },
      memoriesByResidentId: remember(state, "ruta-kalniene", "received-sealed"),
      access: { ...state.access, blueGate: true },
    };
  }
  if (action === "deliver_ruta_opened") {
    if (state.quest.status !== "carrying" || state.quest.parcel !== "opened") {
      return state;
    }
    const helpful = witnessed(state, {
      id: "courtyard-key:ruta-opened",
      tag: "helpful",
      witnessIds: ["ruta-kalniene"],
      source: "quest",
    });
    return {
      ...state,
      ...helpful,
      quest: {
        status: "completed",
        parcel: "delivered",
        outcome: "delivered_opened",
      },
      memoriesByResidentId: remember(state, "ruta-kalniene", "received-opened"),
      access: { ...state.access, archiveConversation: true },
    };
  }
  if (state.quest.status !== "carrying") return state;
  const showOff = witnessed(state, {
    id: "courtyard-key:entrusted-mantas",
    tag: "show_off",
    witnessIds: ["mantas-simkus", "ieva-zukauskaite"],
    source: "quest",
  });
  const odd = witnessed({ ...state, ...showOff }, {
    id: "courtyard-key:mysterious-arrival",
    tag: "odd",
    witnessIds: ["ruta-kalniene"],
    source: "quest",
  });
  return {
    ...state,
    ...odd,
    quest: {
      status: "completed",
      parcel: "delivered",
      outcome: "entrusted_to_mantas",
    },
    memoriesByResidentId: remember(state, "mantas-simkus", "trusted-with-key"),
    access: { ...state.access, rooftopShortcut: true },
  };
}
